import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from "react";
import classNames from "classnames";
import ReactDatetime from "components/CustomDatePicker";
import { useGetInventoryDetail, useUpdateInventory } from "../../../api/inventory/service/index";
import CustomInputOrSelect from "../../../components/CustomInputOrSelect";
import { FormGroup, Input, Label } from "reactstrap";
import Select from "components/Select";
import { useGetStatuses } from "api/inventory-status/service";
import { getSelectStyles } from "../../../utils/selectStyles";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { formatDateForDisplay } from "utils/dateUtils";
import { useGetChannels } from "api/channels/service";
import ChannelsEmbedded from "views/settings/Channels/ChannelsEmbedded";
import { useTranslation } from "react-i18next";

const VehicleTab = forwardRef(
  ({ inventoryId, activeColor = "secondary", permission, isTheme2 = false }, ref) => {
  const { t } = useTranslation();
  const canEditStockNumber = permission?.can_edit_stock_number === true;
  const commonColors = [
    { value: "Beige", label: "Beige" },
    { value: "Black", label: "Black" },
    { value: "Blue", label: "Blue" },
    { value: "Brown", label: "Brown" },
    { value: "Gold", label: "Gold" },
    { value: "Gray", label: "Gray" },
    { value: "Green", label: "Green" },
    { value: "Maroon", label: "Maroon" },
    { value: "Orange", label: "Orange" },
    { value: "Pink", label: "Pink" },
    { value: "Purple", label: "Purple" },
    { value: "Red", label: "Red" },
    { value: "Silver", label: "Silver" },
    { value: "Tan", label: "Tan" },
    { value: "White", label: "White" },
    { value: "Yellow", label: "Yellow" }
  ];
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [isLightTheme, setIsLightTheme] = useState(false);
  const { data: locationsData } = useGetChannels("sales_location_branch");
  const { data: alternateInventoryLocationData } = useGetChannels("alternate_inventory_location");

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const customSelectStyles = useMemo(() => getSelectStyles(activeColorStyle, isLightTheme), [activeColorStyle, isLightTheme]);
  const selectStyles = useMemo(
    () =>
      isTheme2
        ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true)
        : customSelectStyles,
    [isTheme2, activeColorStyle, customSelectStyles]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".vehicle-tab.vehicle-tab--theme2",
            fieldsSelector: ".vehicle-tab-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle = isTheme2
    ? { fontSize: "14px" }
    : { borderRadius: 0, fontSize: "14px" };
  const getDateInputProps = (extra = {}) => ({
    className: "form-control",
    style: isTheme2 ? { fontSize: "14px" } : { borderRadius: 0 },
    ...extra,
  });
  const customFieldTextInputClassName = isTheme2 ? "form-control" : undefined;
  const customFieldTextInputStyle = inputStyle;
  const [formData, setFormData] = useState({
    vin: '',
    new_used: '',
    make: '',
    model: '',
    trim: '',
    interior_trim: '',
    transmission: '',
    engine: '',
    weight: '0',
    year: '',
    exterior_color: '',
    body_style: '',
    type: '',
    condition: '',
    fuel: '',
    exterior_primary_color: '',
    exterior_secondary_color: '',
    interior_color: '',
    notes: '',
    // Lot/Stock/Mileage Information
    primary_lot: '',
    stock_number: '',
    actual_mileage: '',
    actual_mileage_status: '',
    vehicle_status: '',
    // Tag Information
    dealer_temp_tag: '',
    dealer_temp_tag_date: '',
    buyers_temp_tag: '',
    buyers_temp_tag_date: '',
    suppl_buyers_tag: '',
    suppl_buyers_tag_date: '',
    // Secondary Lot Info
    alt_location: '',
    // Additional Vehicle Information
    user_defined: '',
    plate_number: '',
    plate_expiration_date: '',
    inspection_expiration_date: '',
    valuation_number: '',
    emissions_number: '',
    inspected_by_3rd_party: false,
    recovery_functionality: '',
    recovery_type: '',
    recovery_esn: '',
    gps_exp_date: '',
    additional_notes: '',
    contract_date: '',
    deal_id: '',
    calculus_id: '',

  });

  const updateMutation = useUpdateInventory();
  const [isCustomFuel, setIsCustomFuel] = useState(false);
  const [isCustomModel, setIsCustomModel] = useState(false);
  const [isCustomInteriorColor, setIsCustomInteriorColor] = useState(false);
  const [prevValues, setPrevValues] = useState({});
  const { data: statusesData } = useGetStatuses();

  // Dropdown options
  const [dropdownOptions, setDropdownOptions] = useState({
    actualMileageStatusOptions: [
      { value: 'actual', label: 'Actual Miles' },
      { value: 'unknown', label: 'True Mileage Unknown' },
      { value: 'beyond_limits', label: 'Beyond Mechanical Limits' },
      { value: 'exempt', label: 'Exempt' }
    ],
    vehicleStatusOptions: statusesData?.results?.map(status => ({ value: status.status_key, label: status.status_name })),
    interiorColorOptions: []
  });

  const { data, isLoading } = useGetInventoryDetail(inventoryId || '', 'VEHICLE');
  const [showAddLot, setShowAddLot] = useState(false);
  const [showAddAltLocation, setShowAddAltLocation] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({
        // Vehicle Details
        vin: data?.inventory_vehicle?.vin || '',
        new_used: data?.inventory_vehicle?.new_used || '',
        make: data?.inventory_vehicle?.make || '',
        model: data?.inventory_vehicle?.model || '',
        trim: data?.inventory_vehicle?.trim || '',
        interior_trim: data?.inventory_vehicle?.interior_trim || '',
        transmission: data?.inventory_vehicle?.transmission || '',
        engine: data?.inventory_vehicle?.engine || '',
        weight: data?.inventory_vehicle?.weight !== undefined && data?.inventory_vehicle?.weight !== null ? String(data?.inventory_vehicle?.weight) : '0',
        year: data?.inventory_vehicle?.year || '',
        exterior_color: data?.inventory_vehicle?.exterior_color || '',
        body_style: data?.inventory_vehicle?.body_style || '',
        type: data?.inventory_vehicle?.type || '',
        condition: data?.inventory_vehicle?.condition || '',
        fuel: data?.inventory_vehicle?.fuel ? data?.inventory_vehicle?.fuel.toLowerCase() : '',
        exterior_primary_color: data?.inventory_vehicle?.exterior_primary_color || '',
        exterior_secondary_color: data?.inventory_vehicle?.exterior_secondary_color || '',
        interior_color: data?.inventory_vehicle?.interior_color || '',
        additional_notes: data?.inventory_vehicle?.additional_notes || '',
        // Lot/Stock/Mileage Information
        primary_lot: data?.location?.id || '',
        stock_number: data?.stock_number || data?.inventory_vehicle?.stock_number || '',
        actual_mileage: data?.inventory_vehicle?.actual_mileage || '',
        actual_mileage_status: data?.inventory_vehicle?.actual_mileage_status || '',
        vehicle_status: data?.inventory_vehicle?.vehicle_status?.id || '',
        // Tag Information
        dealer_temp_tag: data?.inventory_vehicle?.dealer_temp_tag || '',
        dealer_temp_tag_date: data?.inventory_vehicle?.dealer_temp_tag_date || '',
        buyers_temp_tag: data?.inventory_vehicle?.buyers_temp_tag || '',
        buyers_temp_tag_date: data?.inventory_vehicle?.buyers_temp_tag_date || '',
        suppl_buyers_tag: data?.inventory_vehicle?.suppl_buyers_tag || '',
        suppl_buyers_tag_date: data?.inventory_vehicle?.suppl_buyers_tag_date || '',
        // Secondary Lot Info
        alt_location: data?.inventory_vehicle?.alt_location?.id || '',
        // Additional Vehicle Information
        user_defined: data?.inventory_vehicle?.additional_vehicle_info?.user_defined || '',
        plate_number: data?.inventory_vehicle?.additional_vehicle_info?.plate_number || '',
        plate_expiration_date: data?.inventory_vehicle?.additional_vehicle_info?.plate_expiration_date ? new Date(data?.inventory_vehicle?.additional_vehicle_info?.plate_expiration_date) : '',
        inspection_expiration_date: data?.inventory_vehicle?.additional_vehicle_info?.inspection_expiration_date ? new Date(data?.inventory_vehicle?.additional_vehicle_info?.inspection_expiration_date) : '',
        valuation_number: data?.inventory_vehicle?.additional_vehicle_info?.valuation_number || '',
        emissions_number: data?.inventory_vehicle?.additional_vehicle_info?.emissions_number || '',
        inspected_by_3rd_party: data?.inventory_vehicle?.additional_vehicle_info?.inspected_by_3rd_party || false,
        recovery_functionality: data?.inventory_vehicle?.additional_vehicle_info?.recovery_functionality || '',
        recovery_type: data?.inventory_vehicle?.additional_vehicle_info?.recovery_type || '',
        recovery_esn: data?.inventory_vehicle?.additional_vehicle_info?.recovery_esn || '',
        gps_exp_date: data?.inventory_vehicle?.additional_vehicle_info?.gps_exp_date ? new Date(data?.inventory_vehicle?.additional_vehicle_info?.gps_exp_date) : '',
        dealer_temp_tag: data?.inventory_vehicle?.tag_information?.dealer_temp_tag || '',
        dealer_temp_tag_date: data?.inventory_vehicle?.tag_information?.dealer_temp_tag_date ? new Date(data?.inventory_vehicle?.tag_information?.dealer_temp_tag_date) : '',
        buyers_temp_tag: data?.inventory_vehicle?.tag_information?.buyers_temp_tag || '',
        buyers_temp_tag_date: data?.inventory_vehicle?.tag_information?.buyers_temp_tag_date ? new Date(data?.inventory_vehicle?.tag_information?.buyers_temp_tag_date) : '',
        suppl_buyers_tag: data?.inventory_vehicle?.tag_information?.suppl_buyers_tag || '',
        suppl_buyers_tag_date: data?.inventory_vehicle?.tag_information?.suppl_buyers_tag_date ? new Date(data?.inventory_vehicle?.tag_information?.suppl_buyers_tag_date) : '',
        date_issued: data?.inventory_vehicle?.tag_information?.date_issued ? new Date(data?.inventory_vehicle?.tag_information?.date_issued) : '',
        // Sale Info
        contract_date: data?.contract_date || '',
        deal_id: data?.deal_id || '',
        calculus_id: data?.calculus_id || '',
      });
    }
  }, [data]);



  // Interior Color options'ı API'den gelen veriye göre güncelle
  useEffect(() => {
    if (data?.inventory_vehicle?.interior_color) {
      setDropdownOptions(prev => ({
        ...prev,
        interiorColorOptions: [
          { value: data.inventory_vehicle.interior_color, label: data.inventory_vehicle.interior_color }
        ]
      }));
    }
  }, [data?.inventory_vehicle?.interior_color]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLocationChange = (field, value) => {
    if (value === "add_new_lot") {
      if (field === "primary_lot") {
        setShowAddLot(true);
      } else if (field === "alt_location") {
        setShowAddAltLocation(true);
      }
    } else {
      handleInputChange(field, value);
    }
  };

  // Parent component'e fonksiyonları expose et
  useImperativeHandle(ref, () => ({
    handleSave: async () => {
      const stockNumberPayload = canEditStockNumber
        ? { stock_number: formData.stock_number ?? '' }
        : {};

      const updateData = {
        ...stockNumberPayload,
        location: {
          id: formData.primary_lot || null,
        },
        vehicle: {
          ...stockNumberPayload,
          vin: formData.vin || null,
          new_used: formData.new_used || null,
          year: formData.year || null,
          make: formData.make || null,
          model: formData.model || null,
          trim: formData.trim || null,
          interior_trim: formData.interior_trim || null,
          exterior_color: formData.exterior_color || null,
          exterior_primary_color: formData.exterior_primary_color || null,
          exterior_secondary_color: formData.exterior_secondary_color || null,
          interior_color: formData.interior_color || null,
          body_style: formData.body_style || null,
          drive_type: formData.drive_type || null,
          transmission: formData.transmission || null,
          engine: formData.engine || null,
          fuel: formData.fuel || null,
          weight: formData.weight || null,
          alt_location: formData.alt_location || null,
          type: formData.type || null,
          condition: formData.condition || null,
          actual_mileage: formData.actual_mileage || null,
          actual_mileage_status: formData.actual_mileage_status || null,
          user_defined: formData.user_defined || null,
          vehicle_status: formData.vehicle_status || null,
          additional_notes: formData.additional_notes || null,
          tag_information: {
            dealer_temp_tag: formData.dealer_temp_tag || '',
            dealer_temp_tag_date: formData.dealer_temp_tag_date || '',
            buyers_temp_tag: formData.buyers_temp_tag || '',
            buyers_temp_tag_date: formData.buyers_temp_tag_date || '',
            suppl_buyers_tag: formData.suppl_buyers_tag || '',
            suppl_buyers_tag_date: formData.suppl_buyers_tag_date || '',
            date_issued: formData.date_issued || '',
          },
          additional_vehicle_info: {
            user_defined: formData.user_defined || '',
            plate_number: formData.plate_number || '',
            plate_expiration_date: formData.plate_expiration_date || '',
            inspection_expiration_date: formData.inspection_expiration_date || '',
            valuation_number: formData.valuation_number || '',
            emissions_number: formData.emissions_number || '',
            inspected_by_3rd_party: formData.inspected_by_3rd_party || false,
            recovery_functionality: formData.recovery_functionality || '',
            recovery_type: formData.recovery_type || '',
            recovery_esn: formData.recovery_esn || '',
            gps_exp_date: formData.gps_exp_date || '',
          }
        }
      }
      updateMutation.mutate({ id: inventoryId, data: updateData })
      return { success: true };
    }
  }), [formData, canEditStockNumber, inventoryId, updateMutation]);

  // Boş değerleri '-' ile gösteren fonksiyon
  const showValue = (value) => {
    return value === null || value === undefined || value === '' ? '' : String(value);
  };

  if (isLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames("vehicle-tab", { "vehicle-tab--theme2": isTheme2 })}>
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      {showAddLot && <ChannelsEmbedded
        activeColor={activeColor}
        defaultType="sales_location_branch"
        onClose={() => setShowAddLot(false)}
        onCreated={(created) => {
          if (created?.id) {
            handleInputChange("primary_lot", String(created.id));
          } else if (created?.name) {
            handleInputChange("primary_lot", created.name);
          }
        }}
      />}
      {showAddAltLocation && <ChannelsEmbedded
        activeColor={activeColor}
        defaultType="alternate_inventory_location"
        onClose={() => setShowAddAltLocation(false)}
        onCreated={(created) => {
          if (created?.id) {
            handleInputChange("alt_location", String(created.id));
          } else if (created?.name) {
            handleInputChange("alt_location", created.name);
          }
        }}
      />}
      {!showAddLot && !showAddAltLocation &&
        <div className={classNames("row", { "vehicle-tab-fields": isTheme2 })}>
          {/* VEHICLE DETAILS */}

          {/* LOT/STOCK/MILEAGE INFORMATION */}
          <div className="col-md-6 ">
            <div className="card">
              <div className="card-header font-weight-bold text-uppercase">{t('vehicleTab.lotStockMileageInfo')}</div>
              <div className="card-body">
                <div className="form-group">
                  <div className="row">
                    <div className="col-md-6">
                      <label>{t('vehicleTab.primaryLot')}</label>
                      <Select
                        styles={selectStyles || {}}
                        className="react-select"
                        placeholder={t('vehicleTab.selectLot')}
                        name="primary_lot"
                        isSearchable
                        closeMenuOnSelect={true}
                        value={formData.primary_lot ? {
                          value: formData.primary_lot,
                          label: formData.primary_lot === "add_new_lot"
                            ? t('vehicleTab.addNewLot')
                            : locationsData?.results?.find(loc => loc.id === formData.primary_lot)?.name || formData.primary_lot
                        } : null}
                        onChange={(option) => {
                          if (option?.value === "add_new_lot") {
                            handleLocationChange("primary_lot", "add_new_lot");
                          } else {
                            handleLocationChange("primary_lot", option?.value || "");
                          }
                        }}
                        options={[
                          { value: "add_new_lot", label: t('vehicleTab.addNewLot') },
                          ...(locationsData?.results?.map((location) => ({
                            value: location.id,
                            label: location.name
                          })) || [])
                        ]}
                        isClearable={false}
                      />
                    </div>
                    <div className="col-md-6">
                      <label>{t('vehicleTab.stockNumber')}</label>
                      <input
                        style={inputStyle}
                        type="text"
                        className="form-control"
                        disabled={!canEditStockNumber}
                        value={showValue(formData.stock_number)}
                        onChange={(e) => handleInputChange('stock_number', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <div className="row">
                    <div className="col-md-6">
                      <label>{t('vehicleTab.actualMileage')}</label>
                      <input
                        style={inputStyle}
                        type="text"
                        className="form-control"
                        value={showValue(formData.actual_mileage)}
                        onChange={(e) => handleInputChange('actual_mileage', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label>{t('vehicleTab.actualMileageStatus')}</label>
                      <Select
                        styles={selectStyles || {}}
                        className="react-select"
                        placeholder={`${t('common.select')} ${t('vehicleTab.actualMileageStatus')}`}
                        name="actual_mileage_status"
                        isSearchable={false}
                        closeMenuOnSelect={true}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        value={formData.actual_mileage_status ? {
                          value: formData.actual_mileage_status,
                          label: dropdownOptions.actualMileageStatusOptions.find(opt => opt.value === formData.actual_mileage_status)?.label || formData.actual_mileage_status
                        } : null}
                        onChange={(option) => handleInputChange('actual_mileage_status', option?.value || null)}
                        options={dropdownOptions.actualMileageStatusOptions}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('vehicleTab.vehicleStatus')}</label>
                  <Select
                    styles={selectStyles}
                    className="react-select"
                    placeholder="Select Vehicle Status"
                    name="vehicle_status"
                    isSearchable
                    closeMenuOnSelect={true}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    value={formData.vehicle_status ? {
                      value: formData.vehicle_status,
                      label: statusesData?.results?.find(status => status.id === formData.vehicle_status)?.status_name || formData.vehicle_status
                    } : null}
                    onChange={(option) => handleInputChange('vehicle_status', option?.value || null)}
                    options={statusesData?.results?.map((status) => ({
                      value: status.id,
                      label: status.status_name
                    })) || []}
                  />
                </div>
                {/* SOLD INFO ROW */}
                {(formData.contract_date || formData.deal_id || formData.calculus_id) && (
                  <div className="row mt-3 pb-3">
                    {formData.contract_date && (
                      <div className="col-md-4">
                        <label className="mb-0" style={{ fontSize: '12px', fontWeight: '600', color: isLightTheme ? '#666' : '#a0a0b2' }}>Sold Date:</label>
                        <div className="font-weight-bold" style={{ fontSize: '14px', color: isLightTheme ? '#333' : '#fff' }}>
                          {formatDateForDisplay(formData.contract_date)}
                        </div>
                      </div>
                    )}
                    {formData.deal_id && (
                      <div className="col-md-4">
                        <label className="mb-0" style={{ fontSize: '12px', fontWeight: '600', color: isLightTheme ? '#666' : '#a0a0b2' }}>Deal ID:</label>
                        <div className="font-weight-bold" style={{ fontSize: '14px' }}>
                          <a
                            href={`/deals/${formData.deal_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'none', fontWeight: '600' }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {formData.deal_id}
                          </a>
                        </div>
                      </div>
                    )}
                    {formData.calculus_id && (
                      <div className="col-md-4">
                        <label className="mb-0" style={{ fontSize: '12px', fontWeight: '600', color: isLightTheme ? '#666' : '#a0a0b2' }}>Acct ID:</label>
                        <div className="font-weight-bold" style={{ fontSize: '14px' }}>
                          <a
                            href={`/accounts/${formData.calculus_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'none', fontWeight: '600' }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {formData.calculus_id}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="card">
              <div className="card-header font-weight-bold text-uppercase">{t('vehicleTab.vehicleDetails')}</div>
              <div className="card-body row">
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.vin')}</label>
                  <input
                    style={inputStyle}
                    type="text"
                    className="form-control"
                    value={showValue(formData.vin)}
                    disabled={true}
                    onChange={(e) => handleInputChange('vin', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.newUsed')}</label>
                  <Select
                    styles={selectStyles || {}}
                    placeholder={t('common.select')}
                    name="new_used"
                    isSearchable={false}
                    closeMenuOnSelect={true}
                    value={formData.new_used ? {
                      value: formData.new_used,
                      label: formData.new_used
                    } : null}
                    onChange={(option) => handleInputChange('new_used', option?.value || null)}
                    options={[
                      { value: "New", label: "New" },
                      { value: "Used", label: "Used" }
                    ]}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.year')}</label>
                  <input
                    style={inputStyle}

                    type="number"
                    className="form-control"
                    value={showValue(formData.year)}
                    onChange={(e) => handleInputChange('year', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.make')}</label>
                  <input
                    style={inputStyle}

                    type="text"
                    className="form-control"
                    value={showValue(formData.make)}
                    onChange={(e) => handleInputChange('make', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-6">
                  <CustomInputOrSelect
                    label={t('vehicleTab.model')}
                    value={formData.model}
                    style={inputStyle}
                    onChange={val => handleInputChange('model', val)}
                    options={formData.model ? [{ value: formData.model, label: formData.model }] : []}
                    isCustom={isCustomModel}
                    setIsCustom={setIsCustomModel}
                    prevValue={prevValues.model}
                    setPrevValue={val => setPrevValues(prev => ({ ...prev, model: val }))}
                    fieldKey="model"
                    setFormData={setFormData}
                    customPlaceholder={t('vehicleTab.enterCustomModel')}
                    customSelectStyles={selectStyles}
                    textInputClassName={customFieldTextInputClassName}
                    textInputStyle={customFieldTextInputStyle}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.trim')}</label>
                  <input
                    style={inputStyle}

                    type="text"
                    className="form-control"
                    value={showValue(formData.trim)}
                    onChange={(e) => handleInputChange('trim', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.interiorTrim')}</label>
                  <input
                    style={inputStyle}

                    type="text"
                    className="form-control"
                    value={showValue(formData.interior_trim)}
                    onChange={(e) => handleInputChange('interior_trim', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.transmission')}</label>
                  <input
                    style={inputStyle}

                    type="text"
                    className="form-control"
                    value={showValue(formData.transmission)}
                    onChange={(e) => handleInputChange('transmission', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.engine')}</label>
                  <input
                    style={inputStyle}

                    type="text"
                    className="form-control"
                    value={showValue(formData.engine)}
                    onChange={(e) => handleInputChange('engine', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.weight')}</label>
                  <input
                    style={inputStyle}

                    type="text"
                    className="form-control"
                    value={showValue(formData.weight)}
                    onChange={(e) => handleInputChange('weight', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.condition') || 'Condition'}</label>
                  <Select
                    styles={selectStyles}
                    className="react-select"
                    placeholder={`${t('common.select')} ${t('vehicleTab.condition') || 'Condition'}`}
                    name="condition"
                    isSearchable={false}
                    closeMenuOnSelect={true}
                    value={formData.condition ? {
                      value: formData.condition,
                      label: [
                        { value: 'Unknown', label: t('vehicleTab.conditionUnknown', 'Unknown') },
                        { value: 'Rough', label: t('vehicleTab.conditionRough', 'Rough') },
                        { value: 'Average', label: t('vehicleTab.conditionAverage', 'Average') },
                        { value: 'Clean', label: t('vehicleTab.conditionClean', 'Clean') },
                        { value: 'Extra Clean', label: t('vehicleTab.conditionExtraClean', 'Extra Clean') }
                      ].find(opt => opt.value === formData.condition)?.label || formData.condition
                    } : null}
                    onChange={(option) => handleInputChange('condition', option?.value || '')}
                    options={[
                      { value: 'Unknown', label: t('vehicleTab.conditionUnknown', 'Unknown') },
                      { value: 'Rough', label: t('vehicleTab.conditionRough', 'Rough') },
                      { value: 'Average', label: t('vehicleTab.conditionAverage', 'Average') },
                      { value: 'Clean', label: t('vehicleTab.conditionClean', 'Clean') },
                      { value: 'Extra Clean', label: t('vehicleTab.conditionExtraClean', 'Extra Clean') }
                    ]}
                    isClearable
                  />
                </div>

                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.exteriorColor')}</label>
                  <input
                    style={inputStyle}

                    type="text"
                    className="form-control"
                    value={showValue(formData.exterior_color)}
                    onChange={(e) => handleInputChange('exterior_color', e.target.value)}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.bodyStyle')}</label>
                  <input
                    style={inputStyle}

                    type="text"
                    className="form-control"
                    value={showValue(formData.body_style)}
                    onChange={(e) => handleInputChange('body_style', e.target.value)}
                  />
                </div>

                <div className="form-group col-md-6">
                  <CustomInputOrSelect
                    label={t('vehicleTab.fuel')}
                    value={formData.fuel}
                    style={inputStyle}
                    onChange={val => handleInputChange('fuel', val)}
                    options={formData.fuel ? [{ value: formData.fuel, label: formData.fuel }] : []}
                    isCustom={isCustomFuel}
                    setIsCustom={setIsCustomFuel}
                    prevValue={prevValues.fuel}
                    setPrevValue={val => setPrevValues(prev => ({ ...prev, fuel: val }))}
                    fieldKey="fuel"
                    setFormData={setFormData}
                    customPlaceholder={t('vehicleTab.enterCustomFuelType')}
                    customSelectStyles={selectStyles}
                    textInputClassName={customFieldTextInputClassName}
                    textInputStyle={customFieldTextInputStyle}
                  />
                </div>
                <div className="form-group col-md-6">
                  <CustomInputOrSelect
                    label={t('vehicleTab.interiorColor')}
                    value={formData.interior_color}
                    style={inputStyle}
                    onChange={val => handleInputChange('interior_color', val)}
                    options={dropdownOptions.interiorColorOptions || []}
                    isCustom={isCustomInteriorColor}
                    setIsCustom={setIsCustomInteriorColor}
                    prevValue={prevValues.interior_color}
                    setPrevValue={val => setPrevValues(prev => ({ ...prev, interior_color: val }))}
                    fieldKey="interior_color"
                    setFormData={setFormData}
                    customPlaceholder={t('vehicleTab.enterCustomInteriorColor')}
                    customSelectStyles={selectStyles}
                    textInputClassName={customFieldTextInputClassName}
                    textInputStyle={customFieldTextInputStyle}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.type') || 'Type'}</label>
                  <Select
                    styles={selectStyles}
                    className="react-select"
                    placeholder={`${t('common.select')} ${t('vehicleTab.type') || 'Type'}`}
                    name="type"
                    isSearchable={false}
                    closeMenuOnSelect={true}
                    value={formData.type ? {
                      value: formData.type,
                      label: [
                        { value: 'Unknown', label: t('vehicleTab.typeUnknown', 'Unknown') },
                        { value: 'Car', label: t('vehicleTab.typeCar', 'Car (Vehicle)') },
                        { value: 'Truck', label: t('vehicleTab.typeTruck', 'Truck (Vehicle)') },
                        { value: 'Motorcycle', label: t('vehicleTab.typeMotorcycle', 'Motorcycle') },
                        { value: 'Recreational Vehicle', label: t('vehicleTab.typeRV', 'Recreational Vehicle') },
                        { value: 'Boat', label: t('vehicleTab.typeBoat', 'Boat') },
                        { value: 'Equipment', label: t('vehicleTab.typeEquipment', 'Equipment') },
                        { value: 'Aircraft', label: t('vehicleTab.typeAircraft', 'Aircraft') },
                        { value: 'Powersports', label: t('vehicleTab.typePowersports', 'Powersports') },
                        { value: 'Other', label: t('vehicleTab.typeOther', 'Other') },
                        { value: 'SUV', label: t('vehicleTab.typeSUV', 'SUV') },
                        { value: 'Van', label: t('vehicleTab.typeVan', 'Van') }
                      ].find(opt => opt.value === formData.type)?.label || formData.type
                    } : null}
                    onChange={(option) => handleInputChange('type', option?.value || '')}
                    options={[
                      { value: 'Unknown', label: t('vehicleTab.typeUnknown', 'Unknown') },
                      { value: 'Car', label: t('vehicleTab.typeCar', 'Car (Vehicle)') },
                      { value: 'Truck', label: t('vehicleTab.typeTruck', 'Truck (Vehicle)') },
                      { value: 'Motorcycle', label: t('vehicleTab.typeMotorcycle', 'Motorcycle') },
                      { value: 'Recreational Vehicle', label: t('vehicleTab.typeRV', 'Recreational Vehicle') },
                      { value: 'Boat', label: t('vehicleTab.typeBoat', 'Boat') },
                      { value: 'Equipment', label: t('vehicleTab.typeEquipment', 'Equipment') },
                      { value: 'Aircraft', label: t('vehicleTab.typeAircraft', 'Aircraft') },
                      { value: 'Powersports', label: t('vehicleTab.typePowersports', 'Powersports') },
                      { value: 'Other', label: t('vehicleTab.typeOther', 'Other') },
                      { value: 'SUV', label: t('vehicleTab.typeSUV', 'SUV') },
                      { value: 'Van', label: t('vehicleTab.typeVan', 'Van') }
                    ]}
                    isClearable
                  />
                </div>
                <div className="col-12 pl-1">
                  <h5 className="font-weight-bold ml-0">Common Colors</h5>
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.exteriorPrimaryColor')}</label>
                  <Select
                    styles={selectStyles}
                    className="react-select"
                    placeholder={t('vehicleTab.exteriorPrimaryColor')}
                    name="exterior_primary_color"
                    isSearchable
                    closeMenuOnSelect={true}
                    value={formData.exterior_primary_color ? {
                      value: formData.exterior_primary_color,
                      label: commonColors.find(c => c.value === formData.exterior_primary_color)?.label || formData.exterior_primary_color
                    } : null}
                    onChange={(option) => handleInputChange('exterior_primary_color', option?.value || '')}
                    options={commonColors}
                    isClearable={true}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>{t('vehicleTab.exteriorSecondaryColor')}</label>
                  <Select
                    styles={selectStyles}
                    className="react-select"
                    placeholder={t('vehicleTab.exteriorSecondaryColor')}
                    name="exterior_secondary_color"
                    isSearchable
                    closeMenuOnSelect={true}
                    value={formData.exterior_secondary_color ? {
                      value: formData.exterior_secondary_color,
                      label: commonColors.find(c => c.value === formData.exterior_secondary_color)?.label || formData.exterior_secondary_color
                    } : null}
                    onChange={(option) => handleInputChange('exterior_secondary_color', option?.value || '')}
                    options={commonColors}
                    isClearable={true}
                  />
                </div>
              </div>
            </div>

          </div>

          <div className="col-md-6">
            <div className="card">
              <div className="card-header font-weight-bold text-uppercase">{t('vehicleTab.tagInformation')}</div>
              <div className="card-body">
                <div className="form-group">
                  <div className="row">
                    <div className="col-md-6">
                      <label>{t('vehicleTab.dealerTempTag')}</label>
                      <input
                        style={inputStyle}
                        type="text"
                        className="form-control"
                        value={showValue(formData.dealer_temp_tag)}
                        onChange={(e) => handleInputChange('dealer_temp_tag', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label>{t('vehicleTab.dateIssued')}</label>
                      <ReactDatetime
                        inputProps={getDateInputProps({
                          placeholder: `${t('common.select')} ${t('common.date')}`,
                        })}
                        value={formData.dealer_temp_tag_date}
                        onChange={(date) => handleInputChange('dealer_temp_tag_date', date)}
                        dateFormat="MM-DD-YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <div className="row">
                    <div className="col-md-6">
                      <label>{t('vehicleTab.buyersTempTag')}</label>
                      <input
                        style={inputStyle}
                        type="text"
                        className="form-control"
                        value={showValue(formData.buyers_temp_tag)}
                        onChange={(e) => handleInputChange('buyers_temp_tag', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label>{t('vehicleTab.dateIssued')}</label>
                      <ReactDatetime
                        inputProps={getDateInputProps({
                          placeholder: `${t('common.select')} ${t('common.date')}`,
                        })}
                        value={formData.buyers_temp_tag_date}
                        onChange={(date) => handleInputChange('buyers_temp_tag_date', date)}
                        dateFormat="MM-DD-YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <div className="row">
                    <div className="col-md-6">
                      <label>{t('vehicleTab.supplBuyersTag')}</label>
                      <input
                        style={inputStyle}
                        type="text"
                        className="form-control"
                        value={showValue(formData.suppl_buyers_tag)}
                        onChange={(e) => handleInputChange('suppl_buyers_tag', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label>{t('vehicleTab.dateIssued')}</label>
                      <ReactDatetime
                        inputProps={getDateInputProps({
                          placeholder: `${t('common.select')} ${t('common.date')}`,
                        })}
                        value={formData.suppl_buyers_tag_date}
                        onChange={(date) => handleInputChange('suppl_buyers_tag_date', date)}
                        dateFormat="MM-DD-YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header font-weight-bold text-uppercase">SECONDARY LOT INFO</div>
              <div className="card-body">
                <div className="form-group">
                  <label>{t('vehicleTab.altLocation')}</label>
                  <Select
                    styles={selectStyles || {}}
                    className="react-select"
                    placeholder={t('vehicleTab.selectLot')}
                    name="alt_location"
                    isSearchable
                    closeMenuOnSelect={true}
                    value={formData.alt_location ? {
                      value: formData.alt_location,
                      label: formData.alt_location === "add_new_lot"
                        ? t('vehicleTab.addNewLot')
                        : alternateInventoryLocationData?.results?.find(loc => loc.id === formData.alt_location)?.name || formData.alt_location
                    } : null}
                    onChange={(option) => {
                      if (option?.value === "add_new_lot") {
                        handleLocationChange("alt_location", "add_new_lot");
                      } else {
                        handleLocationChange("alt_location", option?.value || "");
                      }
                    }}
                    options={[
                      { value: "add_new_lot", label: t('vehicleTab.addNewLot') },
                      ...(alternateInventoryLocationData?.results?.map((location) => ({
                        value: location.id,
                        label: location.name
                      })) || [])
                    ]}
                    isClearable={false}
                  />

                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header font-weight-bold text-uppercase">{t('vehicleTab.additionalVehicleInfo')}</div>
              <div className="card-body">
                <div className="form-group">
                  <div className="row">
                    <div className="col-md-6">
                      <label>{t('vehicleTab.userDefined')}</label>
                      <input
                        style={inputStyle}
                        type="text"
                        className="form-control"
                        value={showValue(formData.user_defined)}
                        onChange={(e) => handleInputChange('user_defined', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label>{t('vehicleTab.plateNumber')}</label>
                      <input
                        style={inputStyle}
                        type="text"
                        className="form-control"
                        value={showValue(formData.plate_number)}
                        onChange={(e) => handleInputChange('plate_number', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <div className="row">
                    <div className="col-md-6">
                      <label>{t('vehicleTab.plateExpirationDate')}</label>
                      <ReactDatetime
                        inputProps={getDateInputProps({
                          placeholder: `${t('common.select')} ${t('common.date')}`,
                        })}
                        value={formData.plate_expiration_date}
                        onChange={(date) => handleInputChange('plate_expiration_date', date)}
                        dateFormat="MM-DD-YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                      />
                    </div>
                    <div className="col-md-6">
                      <label>{t('vehicleTab.inspectionExpirationDate')}</label>
                      <ReactDatetime
                        inputProps={getDateInputProps({
                          placeholder: `${t('common.select')} ${t('common.date')}`,
                        })}
                        value={formData.inspection_expiration_date}
                        onChange={(date) => handleInputChange('inspection_expiration_date', date)}
                        dateFormat="MM-DD-YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('vehicleTab.valuationNumber')}</label>
                  <input
                    style={inputStyle}
                    type="text"
                    className="form-control"
                    value={showValue(formData.valuation_number)}
                    onChange={(e) => handleInputChange('valuation_number', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <div className="row">
                    <div className="col-md-6 d-flex flex-column">
                      <FormGroup check className="mt-3">

                        <Label check>
                          <Input
                            style={inputStyle}
                            type="checkbox"
                            className="form-check-input"
                            checked={formData.inspected_by_3rd_party}
                            onChange={(e) => handleInputChange('inspected_by_3rd_party', e.target.checked)}
                          />
                          <span className="form-check-sign" />
                          Inspected by 3rd Party
                        </Label>
                      </FormGroup>
                    </div>
                    <div className="col-md-6">
                      <label>{t('vehicleTab.emissionsNumber')}</label>
                      <input
                        style={inputStyle}
                        type="text"
                        className="form-control"
                        value={showValue(formData.emissions_number)}
                        onChange={(e) => handleInputChange('emissions_number', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <div className="row">
                    <div className="col-md-6">
                      <label>{t('vehicleTab.recoveryType')}</label>
                      <input
                        style={inputStyle}
                        type="text"
                        className="form-control"
                        value={showValue(formData.recovery_type)}
                        onChange={(e) => handleInputChange('recovery_type', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label>{t('vehicleTab.recoveryFunctionality')}</label>
                      <input
                        style={inputStyle}
                        type="text"
                        className="form-control"
                        value={showValue(formData.recovery_functionality)}
                        onChange={(e) => handleInputChange('recovery_functionality', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <div className="row">
                    <div className="col-md-6">
                      <label>{t('vehicleTab.recoveryEsn')}</label>
                      <input
                        style={inputStyle}
                        type="text"
                        className="form-control"
                        value={showValue(formData.recovery_esn)}
                        onChange={(e) => handleInputChange('recovery_esn', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label>{t('vehicleTab.gpsExpDate')}</label>
                      <ReactDatetime
                        inputProps={getDateInputProps({
                          placeholder: `${t('common.select')} ${t('common.date')}`,
                        })}
                        value={formData.gps_exp_date}
                        onChange={(date) => handleInputChange('gps_exp_date', date)}
                        dateFormat="MM-DD-YYYY"
                        timeFormat={false}
                        closeOnSelect={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('vehicleTab.additionalNotes')}</label>
                  <textarea
                    style={inputStyle}
                    className="form-control"
                    maxLength={500}
                    rows={3}
                    value={showValue(formData.additional_notes)}
                    onChange={(e) => handleInputChange('additional_notes', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div >
      }
    </div >
  );
}
);

export default VehicleTab; 